import { useState, useEffect } from 'react';
import { useTranslation } from '../../context/LanguageContext';
import {
  Ticket, Plus, Search, Clock, CheckCircle, AlertCircle,
  MessageSquare, ChevronRight, Loader2, Filter, X, HeadphonesIcon
} from 'lucide-react';
import { getTickets, STATUS_COLORS, PRIORITY_COLORS, TICKET_STATUS } from '../../services/supportService';
import { getStoredUser as getUser } from '../../context/AuthContext';
import PageHeader from '../ui/PageHeader';
import Button from '../ui/Button';
import CreateTicket from './CreateTicket';
import TicketDetail from './TicketDetail';

const Support = () => {
  const { t } = useTranslation();
  const user = getUser();

  // State
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);

  // Load tickets
  const loadTickets = async () => {
    if (!user?.id) return;

    setLoading(true);
    try {
      const result = await getTickets(user.id, {
        status: statusFilter,
        search: searchTerm
      });

      if (result.success) {
        setTickets(result.data || []);
      }
    } catch (error) {
      console.error('Error loading tickets:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTickets();
  }, [user?.id, statusFilter]);

  // Filter tickets by search term
  const filteredTickets = tickets.filter(ticket => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      ticket.subject?.toLowerCase().includes(search) ||
      ticket.description?.toLowerCase().includes(search)
    );
  });

  // Get status icon
  const getStatusIcon = (status) => {
    switch (status) {
      case 'open':
        return <AlertCircle className="w-4 h-4" />;
      case 'in_progress':
        return <Clock className="w-4 h-4" />;
      case 'waiting':
        return <Clock className="w-4 h-4" />;
      case 'resolved':
        return <CheckCircle className="w-4 h-4" />;
      case 'closed':
        return <CheckCircle className="w-4 h-4" />;
      default:
        return <Ticket className="w-4 h-4" />;
    }
  };

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Handle ticket created
  const handleTicketCreated = () => {
    setShowCreateModal(false);
    loadTickets();
  };

  // Handle ticket updated
  const handleTicketUpdated = () => {
    loadTickets();
  };

  return (
    <div className="h-full flex flex-col">
      <PageHeader
        title={t.support?.title || 'Support'}
        subtitle={t.support?.subtitle || 'Créez et suivez vos tickets de support'}
        customIcon={HeadphonesIcon}
        customColor="#6366f1"
      />

      <div className="flex-1 overflow-y-auto p-4">
        {/* Actions bar */}
        <div className="flex flex-col sm:flex-row gap-3 mb-4">
          {/* Search */}
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder={t.support?.searchPlaceholder || 'Rechercher un ticket...'}
              className="w-full pl-10 pr-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 focus:ring-2 focus:ring-purple-500"
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>

          {/* Status filter */}
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800"
          >
            <option value="">{t.support?.allStatuses || 'Tous les statuts'}</option>
            <option value="open">{t.support?.status?.open || 'Ouvert'}</option>
            <option value="in_progress">{t.support?.status?.inProgress || 'En cours'}</option>
            <option value="waiting">{t.support?.status?.waiting || 'En attente'}</option>
            <option value="resolved">{t.support?.status?.resolved || 'Résolu'}</option>
            <option value="closed">{t.support?.status?.closed || 'Fermé'}</option>
          </select>

          {/* Create button */}
          <Button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            {t.support?.createTicket || 'Créer un ticket'}
          </Button>
        </div>

        {/* Tickets list */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-purple-500" />
          </div>
        ) : filteredTickets.length === 0 ? (
          <div className="text-center py-12 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700">
            <Ticket className="w-12 h-12 mx-auto text-gray-400 mb-4" />
            <p className="text-gray-500 dark:text-gray-400 mb-4">
              {searchTerm ? (t.support?.noResults || 'Aucun résultat') : (t.support?.noTickets || 'Aucun ticket')}
            </p>
            {!searchTerm && (
              <Button onClick={() => setShowCreateModal(true)}>
                <Plus className="w-4 h-4 mr-2" />
                {t.support?.createFirstTicket || 'Créer votre premier ticket'}
              </Button>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            {filteredTickets.map((ticket) => {
              const statusColor = STATUS_COLORS[ticket.status] || STATUS_COLORS.open;
              const priorityColor = PRIORITY_COLORS[ticket.priority] || PRIORITY_COLORS.normal;

              return (
                <div
                  key={ticket.id}
                  onClick={() => setSelectedTicket(ticket)}
                  className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 hover:border-purple-300 dark:hover:border-purple-600 cursor-pointer transition-colors"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      {/* Subject */}
                      <h3 className="font-medium text-gray-900 dark:text-white truncate">
                        {ticket.subject}
                      </h3>

                      {/* Description preview */}
                      <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 line-clamp-2">
                        {ticket.description}
                      </p>

                      {/* Meta info */}
                      <div className="flex flex-wrap items-center gap-3 mt-3">
                        {/* Status badge */}
                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${statusColor.bg} ${statusColor.text}`}>
                          {getStatusIcon(ticket.status)}
                          {t.support?.status?.[ticket.status] || ticket.status}
                        </span>

                        {/* Priority badge */}
                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${priorityColor.bg} ${priorityColor.text}`}>
                          {t.support?.priority?.[ticket.priority] || ticket.priority}
                        </span>

                        {/* Category */}
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          {t.support?.category?.[ticket.category] || ticket.category}
                        </span>

                        {/* Message count */}
                        {ticket.messagecount > 0 && (
                          <span className="inline-flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
                            <MessageSquare className="w-3 h-3" />
                            {ticket.messagecount}
                          </span>
                        )}

                        {/* Date */}
                        <span className="text-xs text-gray-400">
                          {formatDate(ticket.createdat)}
                        </span>
                      </div>
                    </div>

                    <ChevronRight className="w-5 h-5 text-gray-400 flex-shrink-0" />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Create Ticket Modal */}
      {showCreateModal && (
        <CreateTicket
          onClose={() => setShowCreateModal(false)}
          onCreated={handleTicketCreated}
        />
      )}

      {/* Ticket Detail Modal */}
      {selectedTicket && (
        <TicketDetail
          ticket={selectedTicket}
          onClose={() => setSelectedTicket(null)}
          onUpdated={handleTicketUpdated}
        />
      )}
    </div>
  );
};

export default Support;
